import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { uniq } from "lodash";
import { Checkbox } from "../Checkbox";
import { Icon } from "../Icon";
import { Toggle } from "../Toggle";
const getValuesWithChildren = (arr, result = []) => {
    arr.forEach((item) => {
        if (item.children && item.children.length > 0) {
            result.push(item.value);
            getValuesWithChildren(item.children, result);
        }
    });
    return result;
};
const hasChildren = (node) => node.children && node.children.length > 0;
const getChildValues = (node) => {
    var _a;
    const values = [];
    const getValues = (childNode) => {
        var _a;
        if (hasChildren(childNode)) {
            (_a = childNode === null || childNode === void 0 ? void 0 : childNode.children) === null || _a === void 0 ? void 0 : _a.forEach((gc) => getValues(gc));
        }
        else {
            values.push(childNode.value);
        }
    };
    (_a = node === null || node === void 0 ? void 0 : node.children) === null || _a === void 0 ? void 0 : _a.forEach((child) => getValues(child));
    return values;
};
const getParentValues = (node) => {
    var _a;
    const values = [node.value];
    const getValues = (childNode) => {
        var _a;
        if (hasChildren(childNode)) {
            values.push(childNode.value);
            (_a = childNode === null || childNode === void 0 ? void 0 : childNode.children) === null || _a === void 0 ? void 0 : _a.forEach((gc) => getValues(gc));
        }
    };
    (_a = node === null || node === void 0 ? void 0 : node.children) === null || _a === void 0 ? void 0 : _a.forEach((child) => getValues(child));
    return values;
};
export const CheckboxTree = (props) => {
    const { disabled, readOnly, nodes, onChange, value, parentToggleValues, onChangeParentToggle, showParentToggles = false, expanded = true, } = props;
    const [expandedOptions, setExpandedOptions] = useState(() => (expanded ? getValuesWithChildren(nodes) : []));
    useEffect(() => {
        const defaultExpanded = expanded ? getValuesWithChildren(nodes) : [];
        setExpandedOptions(defaultExpanded);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded]);
    const toggleChecked = (node) => {
        if (!onChange || disabled || readOnly) {
            return;
        }
        if (hasChildren(node)) {
            const childValues = getChildValues(node);
            const parentValues = getParentValues(node);
            if (getChecked(node)) {
                onChange(value.filter((val) => !childValues.includes(val)));
                if (showParentToggles) {
                    onChangeParentToggle === null || onChangeParentToggle === void 0 ? void 0 : onChangeParentToggle((parentToggleValues === null || parentToggleValues === void 0 ? void 0 : parentToggleValues.filter((val) => !parentValues.includes(val))) || []);
                }
            }
            else {
                onChange(uniq([...value, ...childValues]));
                if (showParentToggles) {
                    onChangeParentToggle === null || onChangeParentToggle === void 0 ? void 0 : onChangeParentToggle(uniq([...(parentToggleValues || []), ...parentValues]));
                }
            }
        }
        else if (value.includes(node.value)) {
            onChange(value.filter((item) => item !== node.value));
        }
        else {
            onChange([...value, node.value]);
        }
    };
    const toggleExpanded = (val) => {
        if (expandedOptions.includes(val)) {
            setExpandedOptions(expandedOptions.filter((item) => item !== val));
        }
        else {
            setExpandedOptions([...expandedOptions, val]);
        }
    };
    const getExpanded = (val) => expandedOptions.includes(val);
    const getChecked = (node) => {
        var _a;
        if (hasChildren(node)) {
            return (_a = node === null || node === void 0 ? void 0 : node.children) === null || _a === void 0 ? void 0 : _a.every((child) => {
                if (hasChildren(child)) {
                    return getChecked(child);
                }
                return getChecked(child);
            });
        }
        return value.includes(node.value);
    };
    const getIndeterminate = (node) => {
        var _a;
        return (_a = node === null || node === void 0 ? void 0 : node.children) === null || _a === void 0 ? void 0 : _a.some((child) => {
            if (hasChildren(child)) {
                return getIndeterminate(child);
            }
            return getChecked(child);
        });
    };
    const toggleParentToggle = (val) => {
        if (getIsParentToggleChecked(val)) {
            onChangeParentToggle === null || onChangeParentToggle === void 0 ? void 0 : onChangeParentToggle((parentToggleValues === null || parentToggleValues === void 0 ? void 0 : parentToggleValues.filter((item) => val !== item)) || []);
        }
        else {
            onChangeParentToggle === null || onChangeParentToggle === void 0 ? void 0 : onChangeParentToggle([...(parentToggleValues || []), val]);
        }
    };
    const getIsParentToggleChecked = (val) => (parentToggleValues === null || parentToggleValues === void 0 ? void 0 : parentToggleValues.includes(val)) || false;
    return (React.createElement(CheckboxListRoot, null, nodes.map((node) => (React.createElement(CheckboxTreeItem, { key: `node${node.value}`, node: node, disabled: disabled, readOnly: readOnly, showParentToggles: showParentToggles, toggleParentToggle: toggleParentToggle, getIsParentToggleChecked: getIsParentToggleChecked, toggleChecked: toggleChecked, toggleExpanded: toggleExpanded, getExpanded: getExpanded, getChecked: getChecked, getIndeterminate: getIndeterminate })))));
};
const CheckboxTreeItem = (props) => {
    var _a;
    const { node, disabled, getExpanded, getChecked, getIndeterminate, toggleChecked, toggleExpanded, readOnly, showParentToggles, getIsParentToggleChecked, toggleParentToggle, } = props;
    const isChecked = getChecked(node);
    const isExpanded = hasChildren(node) && getExpanded(node.value);
    const isIndeterminate = hasChildren(node) && !isChecked && getIndeterminate(node);
    return (React.createElement(CheckboxListItem, null,
        React.createElement(CheckboxContainer, null,
            hasChildren(node) ? (React.createElement(ExpandButton, { "aria-label": isExpanded ? "Collapse" : "Expand", onClick: () => toggleExpanded(node.value) },
                React.createElement(Icon, { src: isExpanded ? "ChevronDown" : "ChevronRight", size: "12px" }))) : (React.createElement(ExpandedPlaceholder, null)),
            React.createElement(CheckboxLabel, null,
                React.createElement(Checkbox, { indeterminate: isIndeterminate, checked: isChecked, onChange: () => toggleChecked === null || toggleChecked === void 0 ? void 0 : toggleChecked(node), readOnly: readOnly, disabled: disabled }),
                React.createElement(CheckboxText, null, node.label)),
            hasChildren(node) && showParentToggles && (React.createElement(ToggleContainer, null,
                React.createElement(Toggle, { disabled: disabled, readOnly: readOnly, checked: getIsParentToggleChecked(node.value), onChange: () => toggleParentToggle(node.value) })))),
        hasChildren(node) &&
            isExpanded &&
            ((_a = node === null || node === void 0 ? void 0 : node.children) === null || _a === void 0 ? void 0 : _a.map((child) => (React.createElement(CheckboxList, { key: `node${child.value}` },
                React.createElement(CheckboxTreeItem, Object.assign({}, props, { node: child }))))))));
};
const ToggleContainer = styled.span `
  margin-left: auto;
  display: flex;
  align-items: center;
`;
const ExpandButton = styled.button `
  border: 0;
  cursor: pointer;
  background: transparent;
  color: ${(props) => props.theme.colors.mainText};
`;
const CheckboxList = styled.ol `
  list-style: none;
  padding-left: 24px;
`;
const CheckboxListRoot = styled.ol `
  padding: 0;
  margin: 0;
`;
const CheckboxLabel = styled.label `
  display: flex;
  align-items: center;
`;
const ExpandedPlaceholder = styled.div `
  width: 24px;
`;
const CheckboxListItem = styled.li `
  list-style: none;
`;
const CheckboxText = styled.div `
  margin-left: ${({ theme }) => `${theme.space[2]}px`};
  line-height: 30px;
`;
const CheckboxContainer = styled.span `
  display: flex;
  width: 100%;
`;
